/**
 * Quaternions and unit vectors as Float64Arrays, so the game layer can describe where you stand
 * without three.js. Layout: quaternions [x, y, z, w], vectors [x, y, z]. All functions write into
 * `out` and return it; nothing allocates.
 */
export type Quat = Float64Array;
export type Vec = Float64Array;

export const quat = (): Quat => Float64Array.of(0, 0, 0, 1);
export const vec = (x = 0, y = 0, z = 0): Vec => Float64Array.of(x, y, z);

export const UP: Vec = vec(0, 1, 0);
export const AXIS_X: Vec = vec(1, 0, 0);
export const AXIS_Z: Vec = vec(0, 0, 1);

export function quatCopy(out: Quat, q: Quat): Quat {
  out[0] = q[0] ?? 0;
  out[1] = q[1] ?? 0;
  out[2] = q[2] ?? 0;
  out[3] = q[3] ?? 1;
  return out;
}

export function quatFromAxisAngle(
  out: Quat,
  ax: number,
  ay: number,
  az: number,
  angle: number,
): Quat {
  const h = angle / 2;
  const s = Math.sin(h);
  out[0] = ax * s;
  out[1] = ay * s;
  out[2] = az * s;
  out[3] = Math.cos(h);
  return out;
}

/** out = a × b (apply b, then a). */
export function quatMultiply(out: Quat, a: Quat, b: Quat): Quat {
  const ax = a[0] ?? 0,
    ay = a[1] ?? 0,
    az = a[2] ?? 0,
    aw = a[3] ?? 1;
  const bx = b[0] ?? 0,
    by = b[1] ?? 0,
    bz = b[2] ?? 0,
    bw = b[3] ?? 1;
  out[0] = ax * bw + aw * bx + ay * bz - az * by;
  out[1] = ay * bw + aw * by + az * bx - ax * bz;
  out[2] = az * bw + aw * bz + ax * by - ay * bx;
  out[3] = aw * bw - ax * bx - ay * by - az * bz;
  return out;
}

const scratch = quat();

/** q = p × q. */
export function quatPremultiply(q: Quat, p: Quat): Quat {
  quatMultiply(scratch, p, q);
  return quatCopy(q, scratch);
}

export function quatNormalize(q: Quat): Quat {
  const x = q[0] ?? 0,
    y = q[1] ?? 0,
    z = q[2] ?? 0,
    w = q[3] ?? 1;
  const l = Math.hypot(x, y, z, w) || 1;
  q[0] = x / l;
  q[1] = y / l;
  q[2] = z / l;
  q[3] = w / l;
  return q;
}

/** The inverse of a unit quaternion. */
export function quatInvert(out: Quat, q: Quat): Quat {
  out[0] = -(q[0] ?? 0);
  out[1] = -(q[1] ?? 0);
  out[2] = -(q[2] ?? 0);
  out[3] = q[3] ?? 1;
  return out;
}

/** Rotates vector v by q. */
export function quatApply(out: Vec, q: Quat, v: Vec): Vec {
  const vx = v[0] ?? 0,
    vy = v[1] ?? 0,
    vz = v[2] ?? 0;
  const qx = q[0] ?? 0,
    qy = q[1] ?? 0,
    qz = q[2] ?? 0,
    qw = q[3] ?? 1;
  const tx = 2 * (qy * vz - qz * vy);
  const ty = 2 * (qz * vx - qx * vz);
  const tz = 2 * (qx * vy - qy * vx);
  out[0] = vx + qw * tx + qy * tz - qz * ty;
  out[1] = vy + qw * ty + qz * tx - qx * tz;
  out[2] = vz + qw * tz + qx * ty - qy * tx;
  return out;
}

/** The rotation that takes unit vector `from` to unit vector `to`. */
export function quatFromUnitVectors(out: Quat, from: Vec, to: Vec): Quat {
  const fx = from[0] ?? 0,
    fy = from[1] ?? 0,
    fz = from[2] ?? 0;
  const tx = to[0] ?? 0,
    ty = to[1] ?? 0,
    tz = to[2] ?? 0;
  let r = fx * tx + fy * ty + fz * tz + 1;
  if (r < 1e-9) {
    r = 0;
    if (Math.abs(fx) > Math.abs(fz)) {
      out[0] = -fy;
      out[1] = fx;
      out[2] = 0;
    } else {
      out[0] = 0;
      out[1] = -fz;
      out[2] = fy;
    }
  } else {
    out[0] = fy * tz - fz * ty;
    out[1] = fz * tx - fx * tz;
    out[2] = fx * ty - fy * tx;
  }
  out[3] = r;
  return quatNormalize(out);
}

/** Spherical interpolation along the shortest arc. */
export function quatSlerp(out: Quat, a: Quat, b: Quat, t: number): Quat {
  let bx = b[0] ?? 0,
    by = b[1] ?? 0,
    bz = b[2] ?? 0,
    bw = b[3] ?? 1;
  const ax = a[0] ?? 0,
    ay = a[1] ?? 0,
    az = a[2] ?? 0,
    aw = a[3] ?? 1;
  let cos = ax * bx + ay * by + az * bz + aw * bw;
  if (cos < 0) {
    cos = -cos;
    bx = -bx;
    by = -by;
    bz = -bz;
    bw = -bw;
  }
  let ka: number, kb: number;
  if (cos > 0.9995) {
    ka = 1 - t;
    kb = t;
  } else {
    const angle = Math.acos(cos);
    const s = Math.sin(angle);
    ka = Math.sin((1 - t) * angle) / s;
    kb = Math.sin(t * angle) / s;
  }
  out[0] = ax * ka + bx * kb;
  out[1] = ay * ka + by * kb;
  out[2] = az * ka + bz * kb;
  out[3] = aw * ka + bw * kb;
  return quatNormalize(out);
}

export function vecSet(out: Vec, x: number, y: number, z: number): Vec {
  out[0] = x;
  out[1] = y;
  out[2] = z;
  return out;
}

export function vecCopy(out: Vec, v: Vec): Vec {
  out[0] = v[0] ?? 0;
  out[1] = v[1] ?? 0;
  out[2] = v[2] ?? 0;
  return out;
}

export function vecNormalize(v: Vec): Vec {
  const l = Math.hypot(v[0] ?? 0, v[1] ?? 0, v[2] ?? 0) || 1;
  v[0] = (v[0] ?? 0) / l;
  v[1] = (v[1] ?? 0) / l;
  v[2] = (v[2] ?? 0) / l;
  return v;
}

export const vecDot = (a: Vec, b: Vec): number =>
  (a[0] ?? 0) * (b[0] ?? 0) + (a[1] ?? 0) * (b[1] ?? 0) + (a[2] ?? 0) * (b[2] ?? 0);

/** Interpolates between two unit vectors along the great circle. */
export function vecSlerp(out: Vec, a: Vec, b: Vec, t: number): Vec {
  const d = Math.max(-1, Math.min(1, vecDot(a, b)));
  const angle = Math.acos(d);
  if (angle < 1e-6) return vecCopy(out, a);
  const s = Math.sin(angle);
  const ka = Math.sin((1 - t) * angle) / s;
  const kb = Math.sin(t * angle) / s;
  out[0] = (a[0] ?? 0) * ka + (b[0] ?? 0) * kb;
  out[1] = (a[1] ?? 0) * ka + (b[1] ?? 0) * kb;
  out[2] = (a[2] ?? 0) * ka + (b[2] ?? 0) * kb;
  return out;
}

/** Whether every component is a finite number and the vector has (about) unit length. */
export function isUnit(v: readonly number[]): boolean {
  if (v.length !== 3) return false;
  const [x, y, z] = v;
  if (x === undefined || y === undefined || z === undefined) return false;
  if (!Number.isFinite(x) || !Number.isFinite(y) || !Number.isFinite(z)) return false;
  const l = Math.hypot(x, y, z);
  return l > 0.98 && l < 1.02;
}
